/**
 * ErrorBoundary.jsx - React Error Boundary Component
 * 
 * @description
 * Catches JavaScript errors anywhere in the component tree and displays
 * a fallback UI instead of crashing the entire application.
 * 
 * @usage
 * Wrap components that might throw errors:
 * ```jsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 * 
 * @features
 * - Catches render errors
 * - Catches lifecycle errors
 * - Displays user-friendly error message
 * - Logs error details to console
 * - Provides recovery options
 * 
 * @errorInfo
 * Captures:
 * - Error message
 * - Component stack trace
 * - Error boundary location
 * 
 * @recovery
 * - Reload page button
 * - Return to home link
 * - Contact support option
 * 
 * @lifecycle
 * 1. componentDidCatch: Logs error details
 * 2. getDerivedStateFromError: Updates state to show fallback UI
 * 3. render: Shows error UI or children
 * 
 * @limitations
 * Does NOT catch:
 * - Event handler errors (use try-catch)
 * - Async code errors (promises)
 * - Server-side rendering errors
 * - Errors in the error boundary itself
 * 
 * @styling
 * - Centered error display
 * - Red accent colors
 * - Responsive layout
 * - Clear action buttons
 * 
 * @production
 * In production, consider:
 * - Sending errors to logging service
 * - More detailed error categorization
 * - User session preservation
 * - Automatic error reporting
 */

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="animate-bounce mb-8">
              <span className="text-6xl">😔</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h1>
            <p className="text-gray-600 mb-8">
              We're sorry, but something unexpected happened. Please try again.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="block w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-center"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 